import { inject, Injectable } from '@angular/core';
import { catchError, forkJoin, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { CustomHttpResponse, DocumentSections, SupplierInvoiceItemMapping } from '../../../models';
import { ApiHttpService } from '../../../services/api-http.service';
import { SectionsFor } from '../../../types';

@Injectable({
  providedIn: 'root',
})
export class SupplierInvoiceItemMappingService {
  apiService: ApiHttpService<SupplierInvoiceItemMapping> = inject(ApiHttpService<SupplierInvoiceItemMapping>);

  getForSupplierInvoice(iri: string): Observable<SupplierInvoiceItemMapping[]> {
    return this.apiService
      .getMany(`${iri}/supplier_invoice_item_mappings`)
      .pipe(map((result) => result.body['hydra:member']));
  }

  post(supplierInvoiceItemMapping: SupplierInvoiceItemMapping): Observable<SupplierInvoiceItemMapping> {
    return this.apiService
      .post('/supplier_invoice_item_mappings', supplierInvoiceItemMapping)
      .pipe(map((result: CustomHttpResponse<SupplierInvoiceItemMapping>) => result.body as SupplierInvoiceItemMapping));
  }

  update(iri: string, supplierInvoiceItemMapping: SupplierInvoiceItemMapping): Observable<SupplierInvoiceItemMapping> {
    return this.apiService
      .patch(iri, supplierInvoiceItemMapping)
      .pipe(map((result: CustomHttpResponse<SupplierInvoiceItemMapping>) => result.body));
  }

  delete(supplierInvoiceItemMappingIri: string) {
    return this.apiService.delete(`${supplierInvoiceItemMappingIri}`).pipe(map((result) => result));
  }

  getForSupplierInvoiceItem(iri: string): Observable<SupplierInvoiceItemMapping[]> {
    return this.apiService
      .getMany(`${iri}/supplier_invoice_item_mappings`)
      .pipe(map((result) => result.body['hydra:member']));
  }

  getForSupplierInvoiceSection(iris: string[]): Observable<DocumentSections<SupplierInvoiceItemMapping>> {
    const requests = iris.map((iri) =>
      this.apiService.getMany(`${iri}/supplier_invoice_item_mappings`).pipe(
        map((res) => ({ iri, items: res.body['hydra:member'] as SupplierInvoiceItemMapping[] })),
        // keep the key even if one call fails
        catchError(() => of({ iri, items: [] })),
      ),
    );

    return forkJoin(requests).pipe(
      map((pairs) => {
        // Start as a partial of the exact-keys type, then return it
        const acc: Partial<SectionsFor<readonly string[], SupplierInvoiceItemMapping>> = {};
        for (const { iri, items } of pairs) acc[iri] = items;
        return acc as SectionsFor<typeof iris, SupplierInvoiceItemMapping>;
      }),
    );
  }
}
